import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const acceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true
    });
  };

  const acceptEssential = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false
    });
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-4 animate-in slide-in-from-bottom">
      <Card className="max-w-4xl mx-auto bg-card border-border shadow-lg">
        <div className="p-3 sm:p-6">
          <div className="flex justify-between items-start mb-2 sm:mb-4 gap-2">
            <h3 className="text-sm sm:text-lg font-semibold">הגדרות קוקיז</h3>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsVisible(false)} 
              className="h-6 w-6 sm:h-8 sm:w-8 shrink-0"
            >
              <X className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </div>

          <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 hidden sm:block">
            אנו משתמשים בקוכיז כדי לשפר את חוויית השימוש שלך.
            למידע נוסף, עיין ב<Link to="/privacy" className="text-primary hover:underline">מדיניות הפרטיות</Link> שלנו.
          </p>
          
          <p className="text-xs text-muted-foreground mb-3 sm:hidden">
            משתמשים בקוקיז לשיפור חווית השימוש. <Link to="/privacy" className="text-primary hover:underline">מדיניות פרטיות</Link>
          </p>

          {!showSettings ? (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button onClick={acceptAll} className="flex-1 h-9 sm:h-10 text-sm">
                אישור
              </Button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-start gap-2 sm:gap-3">
                  <Checkbox id="essential" checked={true} disabled className="mt-0.5 sm:mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <label htmlFor="essential" className="text-xs sm:text-sm font-medium block">
                      הכרחיות
                    </label>
                    <p className="text-xs text-muted-foreground mt-0.5 sm:mt-1 hidden sm:block">
                      נדרשות לתפעול תקין של האתר
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3">
                  <Checkbox 
                    id="analytics" 
                    checked={preferences.analytics} 
                    onCheckedChange={(checked) => setPreferences(prev => ({
                      ...prev,
                      analytics: checked as boolean
                    }))} 
                    className="mt-0.5 sm:mt-1 shrink-0" 
                  />
                  <div className="flex-1 min-w-0">
                    <label htmlFor="analytics" className="text-xs sm:text-sm font-medium block">
                      אנליטיקה
                    </label>
                    <p className="text-xs text-muted-foreground mt-0.5 sm:mt-1 hidden sm:block">
                      עוזרות להבין שימוש באתר
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2 sm:gap-3">
                  <Checkbox 
                    id="marketing" 
                    checked={preferences.marketing} 
                    onCheckedChange={(checked) => setPreferences(prev => ({
                      ...prev,
                      marketing: checked as boolean
                    }))} 
                    className="mt-0.5 sm:mt-1 shrink-0" 
                  />
                  <div className="flex-1 min-w-0">
                    <label htmlFor="marketing" className="text-xs sm:text-sm font-medium block">
                      שיווק
                    </label>
                    <p className="text-xs text-muted-foreground mt-0.5 sm:mt-1 hidden sm:block">
                      פרסומות רלוונטיות
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <Button onClick={saveCustom} className="flex-1 h-9 sm:h-10 text-sm">
                  שמור
                </Button>
                <Button 
                  onClick={() => setShowSettings(false)} 
                  variant="outline" 
                  className="flex-1 h-9 sm:h-10 text-sm"
                >
                  חזור
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CookieConsent;
